import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { FeatherIcon } from '../components/FeatherIcon';
import { ReloginModal } from '../components/Onboarding/ReloginModal';
import { toastQueue } from '../components/ToastProvider';
import { useAccounts } from '../context/AccountsContext';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { useAccountPermissions } from '../dashboard/useAccountPermissions';
import { hasReconnectAccess } from '../permissions';
import { useCrmHost } from './CrmHostContext';
import './CrmLoggedOutBanner.css';

/**
 * The CRM view's notice for an Account whose OnlyFans session is logged out:
 * OnlyFans answers every call with 401 "Access denied.", so the pages behind
 * only show errors. Pinned over the top of the page (frame or React page),
 * with "Log in again" (the console's ReloginModal) for logins allowed to
 * reconnect. It goes away by itself on the next successful OnlyFans answer.
 * `topBar`: under the Fansly-style top bar (CrmTopBar) instead of beside the rail.
 */
export function CrmLoggedOutBanner({ accountId, onReconnected, topBar = false }: { accountId: string; onReconnected: () => void; topBar?: boolean }) {
  const { t } = useTranslation('common');
  const crm = useCrmHost();
  const { sidebarWidth } = useSidebar();
  const isNarrow = useIsNarrow();
  const { isSuperAdmin } = useAuth();
  const { accounts, refresh } = useAccounts();
  const account = useMemo(() => accounts.find((a) => a.account_id === accountId), [accounts, accountId]);
  const scoped = useMemo(() => (account ? [account] : []), [account]);
  const perms = useAccountPermissions(scoped);
  const [relogin, setRelogin] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);

  const loggedOut = crm.isLoggedOut(accountId);
  // An open re-login stays open even if another answer clears the verdict meanwhile.
  if (!loggedOut && !relogin) return null;
  const canReconnect = !!account && (isSuperAdmin || hasReconnectAccess(perms.getPermissions(accountId)));
  const name = account?.username ? `@${account.username}` : account?.profile_name || t('crmLoggedOut.thisAccount');

  return (
    <>
      {loggedOut && dismissed !== accountId && (
        <div className={`crm-logged-out ${topBar ? 'crm-logged-out--topbar' : ''}`} style={isNarrow ? undefined : { left: sidebarWidth + (topBar ? 0 : 60) + 16 }} role="alert">
          <span className="crm-logged-out-icon">
            <FeatherIcon name="log-out" size={16} />
          </span>
          <div className="crm-logged-out-text">
            <strong>{t('crmLoggedOut.title', { name })}</strong>
            <span>{canReconnect ? t('crmLoggedOut.body') : t('crmLoggedOut.bodyAskOwner')}</span>
          </div>
          {canReconnect && (
            <Button size="sm" variant="primary" onPress={() => setRelogin(true)}>
              {t('crmLoggedOut.action')}
            </Button>
          )}
          <button type="button" className="crm-logged-out-close" aria-label={t('crmLoggedOut.dismiss')} onClick={() => setDismissed(accountId)}>
            <FeatherIcon name="x" size={14} />
          </button>
        </div>
      )}
      {relogin && account && (
        <ReloginModal
          accountId={account.account_id}
          platform={account.platform}
          onClose={() => setRelogin(false)}
          onConnected={() => {
            setRelogin(false);
            crm.clearLoggedOut(accountId);
            toastQueue.add({ title: t('crmLoggedOut.reconnected', { name }) });
            void refresh();
            onReconnected();
          }}
        />
      )}
    </>
  );
}
